import { hex2rgba, theme } from 'canoe';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { DOMLayerCanvas, isSelectableNode } from '@candu-ai/placement/dom-layers';
import { cssSelectorForElement } from '@candu-ai/placement/utils';
import { TOOLBOX_ID } from 'craft/containers/Toolbox/constants';

export const updatePreviewElement = (
  previewElement: HTMLDivElement | undefined,
  targetElement: Element | null,
  document: Document | null,
) => {
  if (!previewElement || !targetElement || !document) {
    return;
  }
  /* eslint-disable no-param-reassign */
  const elementPosition = targetElement.getBoundingClientRect();

  previewElement.style.top = `${elementPosition.top + document.documentElement.scrollTop - 2}px`;
  previewElement.style.left = `${elementPosition.left + document.documentElement.scrollLeft - 2}px`;
  previewElement.style.width = `${elementPosition.width}px`;
  previewElement.style.height = `${elementPosition.height}px`;
  /* eslint-enable no-param-reassign */
};

export const addPreviewElementToSnapshot = (snapshotDocument: Document) => {
  const previewElement = snapshotDocument.createElement('div');
  previewElement.style.pointerEvents = 'none';
  previewElement.style.transition = 'top 0.2s ease, opacity 0.5s ease';
  previewElement.style.border = `2px solid ${theme.colors.AquaTeal}`;
  previewElement.style.backgroundColor = hex2rgba(theme.colors.AquaTeal, 0.3);
  previewElement.style.zIndex = '50000000';
  previewElement.style.position = 'absolute';
  previewElement.style.boxSizing = 'content-box';
  previewElement.setAttribute('data-cy-preview-indicator', 'true');

  snapshotDocument.body.appendChild(previewElement);

  return previewElement;
};

const domHighlight = (
  previewElement: HTMLDivElement,
  setHoveredSelector: (selector: string | undefined) => void,
  document: Document,
) => (e: MouseEvent) => {
  if (!e.target) {
    return;
  }
  let { target } = e;

  let isSelectable = isSelectableNode(target as Element);

  // if this element is not selectable (likely Candu content),
  // then check the parent for validity and select that instead
  if (!isSelectable) {
    // @ts-ignore
    target = (target as Element).parentElement;
    isSelectable = isSelectableNode((target as Element)?.parentElement);

    if (!isSelectable) {
      return;
    }
  }

  const updatedHoveredSelector = cssSelectorForElement(target as HTMLElement, document);
  const match = updatedHoveredSelector ? document.querySelector(updatedHoveredSelector) : undefined;

  // run the selector to make sure it matches
  if (match) {
    updatePreviewElement(previewElement, target as HTMLElement, document);
    setHoveredSelector(updatedHoveredSelector);
  }
};

interface InteractivePlacementPickerProps {
  initialValue: string | undefined;
  handleConfirmPlacement: (value: string) => void;
}

export const InteractivePlacementPicker = ({
  initialValue,
  handleConfirmPlacement,
}: InteractivePlacementPickerProps) => {
  const snapshotDocument = useMemo(
    () => (document.getElementById('snapshot-root-frame') as HTMLIFrameElement).contentDocument,
    [],
  ) as Document;
  const snapshotRoot = useMemo(() => snapshotDocument.body, []);

  const [selectedNode, setSelectedNode] = useState<string | undefined>(initialValue);
  const [hoveredNode, setHoveredNode] = useState<string | undefined>();
  const debouncedSetHoveredNode = debounce(setHoveredNode, 20);

  const injectedElement = useRef<HTMLDivElement>();

  useEffect(() => {
    const previewElement = addPreviewElementToSnapshot(snapshotDocument);

    injectedElement.current = previewElement;

    const mouseMoveHandler = domHighlight(
      previewElement,
      debouncedSetHoveredNode,
      snapshotDocument,
    );

    /** Add event listener for the inspector element */
    snapshotDocument.addEventListener('mousemove', mouseMoveHandler);
    return () => {
      injectedElement.current?.remove();
      snapshotDocument.removeEventListener('mousemove', mouseMoveHandler);
    };
  }, []);

  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      e.preventDefault();
      if (!hoveredNode) {
        return;
      }

      injectedElement.current?.remove();
      handleConfirmPlacement(hoveredNode);
    };

    snapshotDocument.addEventListener('click', clickHandler);
    return () => {
      snapshotDocument.removeEventListener('click', clickHandler);
    };
  }, [hoveredNode]);

  const onClickNode = (nodeId: string) => {
    setSelectedNode(nodeId);
  };

  const onHoverNode = (nodeId: string) => {
    const elementMatch = snapshotDocument.querySelector(nodeId);
    updatePreviewElement(injectedElement.current, elementMatch, snapshotDocument);
    debouncedSetHoveredNode(nodeId);
  };

  const onConfirmPlacement = () => {
    if (!selectedNode) {
      return;
    }
    injectedElement.current?.remove();
    handleConfirmPlacement(selectedNode);
  };

  return (
    <DOMLayerCanvas
      context={{ disableScrollForHover: true }}
      document={snapshotDocument}
      node={snapshotRoot}
      panelElement={document.getElementById(TOOLBOX_ID) as HTMLElement}
      selectedNode={selectedNode}
      hoveredNode={hoveredNode}
      onClickNode={onClickNode}
      onHoverNode={onHoverNode}
      onConfirmPlacement={onConfirmPlacement}
    />
  );
};
